/**
 * GMA-1618 (CompassCare Web 3/14 — Vitals triage) — sparkline geometry.
 *
 * Pure geometry for the dense 24h vitals trend sparklines on the Vitals detail
 * panel: map a numeric series to SVG polyline points (x spaced by original
 * index so a missing sample leaves a gap rather than a shifted line; y inverted
 * because SVG y grows downward), an optional normal-range threshold band, and
 * the last-reading marker. UI-free so the <Sparkline> component stays a thin
 * renderer.
 *
 * Fail-safe like the rest of the facelift: a non-array input, missing/non-finite
 * samples, a zero-span domain (all samples equal), and a degenerate size never
 * throw — they resolve to an empty/flat result the component renders blank.
 */

const toFinite = (v) =>
  typeof v === "number" && Number.isFinite(v) ? v : null;

// Positive finite size, else the fallback (guards divide-by-zero + NaN geometry).
const clampSize = (n, fallback) =>
  typeof n === "number" && Number.isFinite(n) && n > 0 ? n : fallback;

// Trim coordinates to 2 decimals — enough for crisp SVG, small in the DOM.
const round = (n) => Math.round(n * 100) / 100;

/**
 * @param {Array<number>} values ordered samples (oldest -> newest)
 * @param {object} [options]
 * @param {number} [options.width=120]
 * @param {number} [options.height=32]
 * @param {number} [options.padding=2] inset so the stroke/marker aren't clipped
 * @param {{low:number,high:number}} [options.band] normal-range band (value space)
 * @param {{min:number,max:number}} [options.domain] force the value domain
 * @returns {{points:string, pointsArray:Array<{x:number,y:number}>,
 *            last:{x:number,y:number}|null, band:{x,y,width,height}|null,
 *            domain:{min:number,max:number}, width:number, height:number}}
 */
export function computeSparkline(values, options = {}) {
  const opts = options ?? {};
  const width = clampSize(opts.width, 120);
  const height = clampSize(opts.height, 32);
  const padReq =
    typeof opts.padding === "number" &&
    Number.isFinite(opts.padding) &&
    opts.padding >= 0
      ? opts.padding
      : 2;
  // Clamp padding per-dimension so `pad * 2` can never exceed the dimension —
  // otherwise a large pad on a small width/height would push x/y outside the
  // viewBox. Cap at (dim - 1)/2 so the inner span stays >= 1.
  const padX = Math.min(padReq, Math.max(0, (width - 1) / 2));
  const padY = Math.min(padReq, Math.max(0, (height - 1) / 2));
  const innerW = Math.max(1, width - padX * 2);
  const innerH = Math.max(1, height - padY * 2);

  const series = Array.isArray(values) ? values : [];
  const n = series.length;

  // Keep finite samples but remember their original index so time-spacing is
  // preserved (a missing sample leaves a gap, not a shifted line).
  const finite = [];
  for (let i = 0; i < n; i++) {
    const v = toFinite(series[i]);
    if (v !== null) finite.push({ v, i });
  }

  // Domain: an explicit override (both bounds finite) wins; else min/max of the
  // finite samples; else a benign 0..0 (renders flat).
  let min;
  let max;
  const domOpt = opts.domain;
  if (domOpt && toFinite(domOpt.min) !== null && toFinite(domOpt.max) !== null) {
    min = domOpt.min;
    max = domOpt.max;
  } else if (finite.length) {
    min = Math.min(...finite.map((s) => s.v));
    max = Math.max(...finite.map((s) => s.v));
  } else {
    min = 0;
    max = 0;
  }
  // Normalize an inverted override (min > max) so `span` can't go negative and
  // map values outside the 0..height range. Computed min/max are already ordered.
  if (min > max) {
    const t = min;
    min = max;
    max = t;
  }
  const span = max - min;

  const xAt = (i) => (n <= 1 ? padX + innerW / 2 : padX + (i / (n - 1)) * innerW);
  // Zero span -> flat line at mid-height (never divide by zero).
  const yAt = (v) => (span === 0 ? padY + innerH / 2 : padY + (1 - (v - min) / span) * innerH);

  const pointsArray = finite.map((s) => ({ x: round(xAt(s.i)), y: round(yAt(s.v)) }));
  // A polyline needs two points; a lone reading shows only its marker.
  const points =
    pointsArray.length >= 2 ? pointsArray.map((p) => `${p.x},${p.y}`).join(" ") : "";
  const last = pointsArray.length ? pointsArray[pointsArray.length - 1] : null;

  // Threshold (normal-range) band as a full-width rect between the mapped
  // low/high. `high` maps to a smaller y than `low` (y inverted), so order the
  // rect from the smaller y with a non-negative height.
  let band = null;
  const b = opts.band;
  if (b && toFinite(b.low) !== null && toFinite(b.high) !== null) {
    const yHigh = round(yAt(b.high));
    const yLow = round(yAt(b.low));
    band = {
      x: 0,
      y: Math.min(yHigh, yLow),
      width,
      height: round(Math.abs(yLow - yHigh)),
    };
  }

  return { points, pointsArray, last, band, domain: { min, max }, width, height };
}
